import './App.css';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BookOpen, Users as UsersIcon, LogOut, Search } from 'lucide-react';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import UsersPage from './pages/Users';
import AppetiteSearch from './pages/AppetiteSearch';

const API = process.env.REACT_APP_BACKEND_URL;

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="w-8 h-8 border-[3px] border-gray-200 border-t-[#101C47] rounded-full animate-spin" />
  </div>
);

function Shell({ user, onLogout, children }) {
  const { pathname } = useLocation();
  const navLink = (to, icon, label, testid) => (
    <Link to={to} data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${pathname === to ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>
      {icon} {label}
    </Link>
  );
  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <header className="h-12 bg-[#101C47] flex items-center px-4 gap-3 flex-shrink-0" data-testid="app-header">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center"><BookOpen size={14} className="text-white" /></div>
          <span className="text-white font-bold text-sm tracking-tight">Appetite Search</span>
        </div>
        <nav className="flex items-center gap-1 ml-4">
          {navLink('/', <Search size={12} />, 'Search', 'nav-search')}
          {user.role === 'admin' && navLink('/users', <UsersIcon size={12} />, 'Users', 'nav-users')}
        </nav>
        <div className="flex-1" />
        <span className="text-white/50 text-xs hidden sm:block" data-testid="current-user-name">{user.name || user.email}</span>
        <button onClick={onLogout} data-testid="logout-btn"
          className="flex items-center gap-1 text-white/60 hover:text-white text-xs px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <LogOut size={12} /> Logout
        </button>
      </header>
      <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null); // null = checking, false = guest

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setUser(false); return; }
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setUser)
      .catch(() => { localStorage.removeItem('token'); setUser(false); });
  }, []);

  const handleLogout = () => { localStorage.removeItem('token'); setUser(false); };

  if (user === null) return <Loader />;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login onLogin={setUser} />} />
          <Route path="/" element={user ? <Shell user={user} onLogout={handleLogout}><AppetiteSearch user={user} /></Shell> : <Navigate to="/login" replace />} />
          <Route path="/users" element={user && user.role === 'admin' ? <Shell user={user} onLogout={handleLogout}><UsersPage currentUser={user} /></Shell> : <Navigate to={user ? '/' : '/login'} replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
